/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, FormControl, Grid, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { LAST_NAMES, NAME, PHONE_NUMBER, EMAIL } from "@utils/expreciones";
import { useFormik } from "formik";
import * as yup from "yup";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import {
  useLazyGetPersonAvalesQuery,
  useLazyGetPersonByIdQuery,
  useLazyGetPersonsQuery,
  usePatchPersonMutation,
  usePostPersonAvalAditionalMutation,
} from "@api/personasApi";
import { useNavigate } from "react-router-dom";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";

import { usePostValidateNextStageMutation } from "@api/applications";
import { PayloadPersonPatch } from "@interfaces/index";

const ESPERANDO_SEGUNDO_AVAL = "CSLSA";
const SEGUNDO_AVAL_CODE = "PTSAV";

const AvalAdicional = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const shareholder = useSelector((state: RootState) => state.secondGuarante.selectedAval);

  const [postPersonAvalAditional] = usePostPersonAvalAditionalMutation();

  const [patchAval] = usePatchPersonMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getPersonAvales] = useLazyGetPersonAvalesQuery();

  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getPersons] = useLazyGetPersonsQuery();

  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      last_name: "",
      last_name_2: "",
      email: "",
      country_code: "+52",
      phone: "",
    },
    validationSchema: yup.object().shape({
      name: yup.string().required("Campo requerido").matches(new RegExp(NAME), "Nombre inválido, revisa el formato"),
      last_name: yup
        .string()
        .required("Campo requerido")
        .matches(new RegExp(LAST_NAMES), "Apellido paterno inválido, revisa el formato"),
      last_name_2: yup.string().matches(new RegExp(LAST_NAMES), "Apellido materno inválido, revisa el formato"),
      email: yup.string().required("Campo requerido").matches(EMAIL, "Correo electrónico inválido"),
      country_code: yup.string().required("Campo requerido"),
      phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        const payload: PayloadPersonPatch = {
          name: values.name,
          last_name: values.last_name,
          last_name_2: values.last_name_2,
          email: values.email,
          code_phone_number: values.country_code,
          phone_number: values.phone,
          loan_application_id: applicationData.id,
        };

        await patchAval({
          personId: String(shareholder?.id),
          body: payload,
        }).unwrap();

        await postPersonAvalAditional({
          body: {
            loan_application_id: applicationData.id,
            person_id: String(shareholder?.id),
            name: values.name,
            last_name: values.last_name,
            last_name_2: values.last_name_2,
            code_phone_number: values.country_code,
            phone_number: values.phone,
            email: values.email,
          },
        }).unwrap();

        const registered = await validateAvalAdicionalRegister();

        if (registered) {
          await triggerPostNextStage({
            applicationId: applicationData.id,
            statusCode: ESPERANDO_SEGUNDO_AVAL,
            username: `TOFU - ${accountUser.email}`,
          }).unwrap();

          navigate("/producto/creditosimple/esperando_segundo_aval");
        } else {
          navigate("/producto/creditosimple/solicitando_segundo_aval");
        }
      } catch (error) {
        const api_error = error as ApiError;
        if (api_error.status && api_error.data) {
          if (api_error.data?.code === "002") {
            formik.setFieldError("phone", "El teléfono ya se encuentra registrado, intenta con otro número");
            return;
          }

          if (api_error?.status === 409) {
            formik.setFieldError("email", "El correo electrónico ya se encuentra registrado, intenta con otro correo");
            return;
          }
        }

        Sentry.captureException(error, {
          extra: {
            component: "InformacionSegundoAval-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const res_person = await getPersonById(String(shareholder?.id)).unwrap();

        formik.setValues({
          name: res_person.name || "",
          last_name: res_person.last_name || "",
          last_name_2: res_person.last_name_2 || "",
          email: res_person.email || "",
          country_code: res_person.code_phone_number || "+52",
          phone: res_person.phone_number || "",
        });
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionSegundoAval-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const validateAvalAdicionalRegister = async (): Promise<boolean> => {
    try {
      const res_persons = await getPersons({
        loanApplicationId: applicationData.id,
        personTypeCode: [SEGUNDO_AVAL_CODE],
      }).unwrap();

      const avalesPorPersona = await Promise.all(
        res_persons.map((person) => getPersonAvales(String(person.id)).unwrap()),
      );

      const allPersonsHaveAvales = res_persons.length > 0 && avalesPorPersona.every((avales) => avales.length > 0);

      return allPersonsHaveAvales;
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "InformacionSegundoAval-initial_fetch",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
      return false;
    }
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Solicitud de Aval Adicional
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            Te solicitamos ingresar un segundo aval a tu solicitud.
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <FormControl fullWidth>
              <TextField
                id="name"
                name="name"
                label="Nombre"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                value={formik.values.name}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="last_name"
                name="last_name"
                label="Apellido paterno"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                helperText={formik.touched.last_name && formik.errors.last_name}
                value={formik.values.last_name}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="last_name_2"
                name="last_name_2"
                label="Apellido materno"
                margin="normal"
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.last_name_2 && Boolean(formik.errors.last_name_2)}
                helperText={formik.touched.last_name_2 && formik.errors.last_name_2}
                value={formik.values.last_name_2}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="email"
                name="email"
                label="Correo electrónico"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const value_to_set = (e.target.value || "").toLowerCase();
                  formik.setFieldValue("email", value_to_set);
                }}
                value={formik.values.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email ? formik.errors.email : ""}
                inputProps={{ maxLength: 100 }}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <Grid container spacing={1} mt={1}>
              <Grid item xs={4} sm={4} md={4}>
                <CustomContryCode
                  label="Código"
                  name="country_code"
                  value={formik.values.country_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={8} sm={8} md={8}>
                <TextField
                  type="text"
                  name="phone"
                  variant="outlined"
                  label="Teléfono"
                  fullWidth
                  required
                  value={formik.values.phone}
                  onChange={(event) => {
                    formik.setFieldValue("phone", event.target.value.replace(/[^0-9]/g, ""));
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                  }}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>
            </Grid>

            <Typography
              textAlign="center"
              mt={4}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={400}
              color={theme?.palette?.primary?.dark}
            >
              En caso de que necesites enviar un enlace al aval adicional para que realice el flujo haz click en enviar.
            </Typography>

            <Box mt={4} mb={4} textAlign="center" justifyContent={"center"}>
              <Button
                variant="default"
                type="submit"
                disabled={!formik.isValid || formik.isSubmitting}
                sx={{ width: 200 }}
              >
                Enviar
              </Button>
            </Box>
          </form>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default AvalAdicional;
